import { IPaymentBreakdownItem } from '@interfaces/payment-breakdown-item';
import { EntrySummaryDutiesModel, EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { BillItemPendingModel } from '@data-layer/models/payments';
import { enumBillItemProductCode } from '@data-layer/enums/payments';
import { IsfV1Model } from '@data-layer/models/isf-v1';
import { enumEntrySummarySubmissionType, enumEntrySummaryType, enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { ManagementEntrySummaryModel, ManagementIsfModel } from '@data-layer/models/management';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { BondRequestModel } from '@data-layer/models/common';
import { enumBondRequestState } from '@data-layer/enums/common';
import { IPaymentBreakdownItemGroup } from '@interfaces/payment-breakdown-group';
import { IsfModel } from '@data-layer/models/isf';

export class PaymentBreakdownHelper {
  public static buildPaymentBreakdownFromPendingBills(
    entity: EntrySummaryModel | IsfV1Model | IsfModel | ManagementEntrySummaryModel | ManagementIsfModel | BondRequestModel,
    pendingBills: BillItemPendingModel[],
    canceledBills: BillItemPendingModel[],
  ): IPaymentBreakdownItemGroup[] {
    let result: IPaymentBreakdownItem[] = [];
    if (
      pendingBills.some((bill) => bill.paymentType === enumCompanyPaymentType.MONTHLY) &&
      pendingBills.some((bill) => bill.paymentType === enumCompanyPaymentType.INSTANT)
    ) {
      result.push({
        header: 'PERIOD_PAYMENT_TYPE.MONTHLY',
        type: 'header',
        discountGroupId: enumCompanyPaymentType.MONTHLY.toString(),
      });
    }

    result = [
      ...result,
      ...this.buildPaymentBreakdown(
        entity,
        pendingBills.filter((bill) => bill.paymentType === enumCompanyPaymentType.MONTHLY),
        'default',
        enumCompanyPaymentType.MONTHLY,
      ),
    ];

    if (
      pendingBills.some((bill) => bill.paymentType === enumCompanyPaymentType.MONTHLY) &&
      pendingBills.some((bill) => bill.paymentType === enumCompanyPaymentType.INSTANT)
    ) {
      result.push({
        header: 'LABELS.INSTANT',
        type: 'header',
        discountGroupId: enumCompanyPaymentType.INSTANT.toString(),
      });
    }

    result = [
      ...result,
      ...this.buildPaymentBreakdown(
        entity,
        pendingBills.filter((bill) => bill.paymentType === enumCompanyPaymentType.INSTANT),
        'default',
        enumCompanyPaymentType.INSTANT,
      ),
    ];

    if (canceledBills.length) {
      result = [...result, ...this.buildPaymentBreakdown(entity, canceledBills, 'canceled')];
    }

    return this.getBillsByGroup(result);
  }

  private static buildPaymentBreakdown(
    entity: EntrySummaryModel | IsfV1Model | IsfModel | ManagementEntrySummaryModel | ManagementIsfModel | BondRequestModel,
    pendingBills: BillItemPendingModel[],
    priceType: IPaymentBreakdownItem['priceType'],
    paymentType?: enumCompanyPaymentType,
  ): IPaymentBreakdownItem[] {
    const dutiesItems: IPaymentBreakdownItem[] = [];
    if (pendingBills.some((bill) => bill.productCode === enumBillItemProductCode.ES_DUTIES)) {
      const es: EntrySummaryModel = entity as any;
      dutiesItems.push(...this.buildDutiesPaymentBreakdown(es.duties, es.type, es.transportationMode, priceType, paymentType));
    }
    const showBondMessage =
      (entity as EntrySummaryModel).submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED &&
      (entity as EntrySummaryModel).bondRequest &&
      (entity as EntrySummaryModel).bondRequest.state !== enumBondRequestState.COMPLETED;
    return [
      ...dutiesItems,
      ...pendingBills
        .filter((bill) => bill.productCode !== enumBillItemProductCode.ES_DUTIES)
        .map((bill) => this.getPaymentBreakdownItemFromBill(bill, showBondMessage, priceType)),
    ];
  }

  private static getPaymentBreakdownItemFromBill(
    bill: BillItemPendingModel,
    showBondMessage: boolean = false,
    priceType: IPaymentBreakdownItem['priceType'],
  ): IPaymentBreakdownItem {
    return {
      quantity: bill.quantity,
      header: bill.description,
      type: 'price',
      price: bill.price,
      hint:
        bill.productCode === enumBillItemProductCode.BROKER_ENTRY && showBondMessage
          ? 'PAYMENT_BREAKDOWN.BROKER_ENTRY_BOND_REQUEST_HINT'
          : undefined,
      priceType,
      notes: bill.notes,
      discountGroupId: bill.discountGroupId || bill.paymentType.toString(),
      code: bill.productCode,
      paymentType: bill.paymentType,
    };
  }

  private static buildDutiesPaymentBreakdown(
    duties: EntrySummaryDutiesModel,
    entrySummaryType: enumEntrySummaryType,
    transportationMode: enumTransportationMode,
    priceType: IPaymentBreakdownItem['priceType'],
    paymentType?: enumCompanyPaymentType,
  ): IPaymentBreakdownItem[] {
    if (!duties) {
      return [];
    }
    const dutiesItems: IPaymentBreakdownItem[] = [
      {
        header: 'PAYMENT_BREAKDOWN.DUTY_AMOUNT',
        price: duties.dutyAmount,
        type: 'price',
        quantity: 1,
        priceType,
        paymentType,
        discountGroupId: paymentType.toString(),
      },
    ];

    if (
      entrySummaryType === enumEntrySummaryType.FORMAL ||
      (entrySummaryType === enumEntrySummaryType.INFORMAL && duties.informalMerchandiseProcessingFee)
    ) {
      dutiesItems.push({
        header: 'PAYMENT_BREAKDOWN.MERCHANDISE_PROCESSING_FEE',
        price:
          entrySummaryType === enumEntrySummaryType.FORMAL
            ? duties.formalMerchandiseProcessingFee || 0
            : duties.informalMerchandiseProcessingFee || 0,
        type: 'price',
        quantity: 1,
        priceType,
        paymentType,
        discountGroupId: paymentType.toString(),
      });
    }

    if (duties.harborMaintenanceFee && transportationMode === enumTransportationMode.OCEAN) {
      dutiesItems.push({
        header: 'PAYMENT_BREAKDOWN.HARBOR_MAINTENANCE_FEE',
        price: duties.harborMaintenanceFee,
        type: 'price',
        quantity: 1,
        priceType,
        paymentType,
        discountGroupId: paymentType.toString(),
      });
    }

    if (duties.internalRevenueTax) {
      dutiesItems.push({
        header: 'PAYMENT_BREAKDOWN.INTERNAL_REVENUE_TAX',
        price: duties.internalRevenueTax,
        type: 'price',
        quantity: 1,
        priceType,
        paymentType,
        discountGroupId: paymentType.toString(),
      });
    }

    if (duties.userFee) {
      dutiesItems.push({
        header: 'PAYMENT_BREAKDOWN.USER_FEE',
        price: duties.userFee,
        type: 'price',
        quantity: 1,
        priceType,
        paymentType,
        discountGroupId: paymentType.toString(),
      });
    }

    return dutiesItems.length > 0
      ? [
          {
            header: 'PAYMENT_BREAKDOWN.TOTAL_DUTIES_AND_FEES',
            type: 'header',
            priceType,
            discountGroupId: paymentType.toString(),
          },
          ...dutiesItems,
        ]
      : [];
  }

  private static getBillsByGroup(breakdownItems: IPaymentBreakdownItem[]): IPaymentBreakdownItemGroup[] {
    return breakdownItems.reduce((result: IPaymentBreakdownItemGroup[], item: IPaymentBreakdownItem) => {
      const foundGroup: IPaymentBreakdownItemGroup = result.find(
        (g: IPaymentBreakdownItemGroup) => g.discountGroupId === item.discountGroupId,
      );

      if (!foundGroup) {
        result.push({ discountGroupId: item.discountGroupId, items: [item] });
        return result;
      }

      foundGroup.items = [...foundGroup.items, item];

      return result;
    }, []);
  }
}
